import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PhilosophyStrip } from "@/components/PhilosophyStrip";
import { pillars } from "@/data/pillars";

const HERO_IMG = "https://images.unsplash.com/photo-1517089152318-42ec560349c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        description="Slatech Solutions connects industrial customers with trusted manufacturers, suppliers, contractors, and service providers across oil & gas, drilling, manufacturing, fabrication, and mining in Alberta, Texas, and North America."
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Industrial drilling operations" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-20">
          <Reveal>
            <p className="text-xs sm:text-sm font-bold tracking-[0.3em] uppercase text-blue-500 mb-6">
              Automation • Capital Equipment • Industrial Sourcing
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading font-black uppercase text-white leading-[0.9] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl">
              Understanding the Work.
              <span className="block text-blue-500">Improving Performance.</span>
              Building the Future.
            </h1>
          </Reveal>
          <Reveal delay={260}>
            <p className="mt-8 font-heading text-2xl sm:text-3xl uppercase tracking-wide text-zinc-200">
              Backed by Trust.
            </p>
          </Reveal>
          <Reveal delay={360}>
            <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
              Slatech Solutions was built to help manufacturers, fabricators, and industrial businesses
              adopt technologies that improve productivity, safety, and quality.
            </p>
          </Reveal>
          <Reveal delay={460}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta"
                className="inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 transition-colors duration-200 group"
              >
                Get In Touch
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-link"
                className="inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest border border-white/30 hover:border-white text-white px-8 py-4 transition-colors duration-200"
              >
                Our Services
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-8 inset-x-0 hidden md:block">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="h-px bg-white/10" />
            <div className="flex flex-wrap gap-x-10 gap-y-2 pt-4 text-xs uppercase tracking-[0.2em] text-zinc-400 font-heading">
              <span>Oil &amp; Gas</span><span>Drilling</span><span>Manufacturing</span>
              <span>Fabrication</span><span>Mining</span><span>Industrial</span>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO / ABOUT TEASER */}
      <section data-testid="about-teaser" className="bg-background py-20 md:py-32 bg-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-4">Who We Are</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">
              A relationship-driven industrial solutions company
            </h2>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
              Slatech Solutions is built on a simple belief: the best solutions are built on strong
              relationships. We bridge the gap between customers, suppliers, manufacturers, and
              industry partners — identifying opportunities and connecting the right people to solve
              complex challenges.
            </p>
            <Link
              to="/about"
              data-testid="about-teaser-link"
              className="mt-8 inline-flex items-center gap-2 font-heading text-lg font-semibold uppercase tracking-widest text-white hover:text-blue-500 transition-colors duration-200 group"
            >
              Read our story
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </Reveal>
          <Reveal delay={150} className="relative">
            <div className="aspect-[4/5] overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1698664683348-f9f35b809821?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1100&h=1375&q=85"
                alt="Welder in full safety gear welding steel in a fabrication shop"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-blue-600 p-6 md:p-8 hidden sm:block">
              <p className="font-heading text-white text-xl font-bold uppercase tracking-tight leading-tight max-w-[180px]">
                Built on industry knowledge &amp; trust
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section data-testid="services-preview" className="bg-black py-20 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-4">What We Do</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-2xl">
                Three ways we help
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-preview-link"
              className="inline-flex items-center gap-2 font-heading text-base font-semibold uppercase tracking-widest text-zinc-300 hover:text-blue-500 transition-colors duration-200 whitespace-nowrap"
            >
              Explore our services <ArrowRight size={18} />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal
                  key={p.id}
                  delay={i * 120}
                  className="group bg-zinc-950 hover:bg-zinc-900 p-8 transition-colors duration-300 relative flex flex-col"
                >
                  <span className="text-blue-500 group-hover:text-blue-400 transition-colors duration-300">
                    <Icon size={36} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mt-6 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{p.description}</p>
                  <ul className="mt-6 space-y-2.5 pt-6 border-t border-white/10">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-zinc-300 text-sm">
                        <span className="text-blue-500 shrink-0"><Check size={16} strokeWidth={2.5} /></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <PhilosophyStrip />

      {/* CTA */}
      <section data-testid="home-cta" className="relative bg-background py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
              Tell us about your challenge
            </h2>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
              No pressure. No obligation. Just a conversation.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="mt-10 inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 transition-colors duration-200 group"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
